import React, { useEffect, useRef, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import {
  borrarDigitoNroCheque,
  empujarDigitosNroCheque,
  formatNroCheque,
  limitarNroCheque,
  NRO_CHEQUE_LEN,
} from '../../utils/nroCheque';

type NroChequeTextFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value?: string | number | null;
  onChange: (value: string) => void;
  /**
   * Alta: los dígitos entran a la derecha y corren a la izquierda.
   * Ver / editar: edición libre, máximo 8.
   */
  empujar?: boolean;
};

const NroChequeTextField: React.FC<NroChequeTextFieldProps> = ({
  value,
  onChange,
  empujar = false,
  inputProps,
  inputRef,
  onFocus,
  onBlur,
  label = 'N° Cheque',
  ...rest
}) => {
  const focusedRef = useRef(false);
  const innerRef = useRef<HTMLInputElement | null>(null);
  const handledRef = useRef(false);
  const [draft, setDraft] = useState(() => formatNroCheque(value));
  const shown = formatNroCheque(value);

  const setInputRef = (el: HTMLInputElement | null) => {
    innerRef.current = el;
    if (typeof inputRef === 'function') inputRef(el);
    else if (inputRef) (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
  };

  useEffect(() => {
    if (!empujar && !focusedRef.current) setDraft(formatNroCheque(value));
  }, [value, empujar]);

  useEffect(() => {
    if (!empujar) return;
    const el = innerRef.current;
    if (el && document.activeElement === el) {
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [shown, empujar]);

  const applyEmpuje = (next: string) => {
    handledRef.current = true;
    onChange(next);
    requestAnimationFrame(() => {
      handledRef.current = false;
    });
  };

  if (empujar) {
    const handleBeforeInput = (e: React.InputEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (handledRef.current) {
        e.preventDefault();
        return;
      }
      const ne = e.nativeEvent as InputEvent;
      const inputType = ne.inputType || '';
      const data = ne.data ?? '';
      if (inputType === 'insertFromPaste') return;
      if (inputType === 'insertText' || inputType === 'insertCompositionText') {
        e.preventDefault();
        if (/^\d+$/.test(data)) applyEmpuje(empujarDigitosNroCheque(shown, data));
        return;
      }
      if (inputType.startsWith('delete')) {
        e.preventDefault();
        applyEmpuje(borrarDigitoNroCheque(shown));
      }
    };

    return (
      <TextField
        {...rest}
        label={label}
        value={shown}
        inputRef={setInputRef}
        onKeyDown={(e) => {
          if (e.key === 'Tab' || e.key === 'Enter' || e.ctrlKey || e.metaKey || e.altKey) return;
          if (/^\d$/.test(e.key)) {
            e.preventDefault();
            applyEmpuje(empujarDigitosNroCheque(shown, e.key));
            return;
          }
          if (e.key === 'Backspace' || e.key === 'Delete') {
            e.preventDefault();
            applyEmpuje(borrarDigitoNroCheque(shown));
            return;
          }
          if (e.key.length === 1) e.preventDefault();
        }}
        onPaste={(e) => {
          e.preventDefault();
          applyEmpuje(empujarDigitosNroCheque(shown, e.clipboardData.getData('text')));
        }}
        onChange={(e) => {
          if (handledRef.current) return;
          const digits = String(e.target.value ?? '').replace(/\D/g, '');
          const prevSig = shown.replace(/^0+/, '');
          const nextSig = digits.replace(/^0+/, '');
          if (nextSig.length <= prevSig.length) {
            applyEmpuje(formatNroCheque(digits));
            return;
          }
          applyEmpuje(empujarDigitosNroCheque(shown, nextSig.slice(prevSig.length)));
        }}
        onFocus={(e) => {
          const el = e.currentTarget;
          requestAnimationFrame(() => el.setSelectionRange(el.value.length, el.value.length));
          onFocus?.(e);
        }}
        onBlur={onBlur}
        inputProps={{
          inputMode: 'numeric',
          autoComplete: 'off',
          maxLength: NRO_CHEQUE_LEN + 1,
          ...inputProps,
          onBeforeInput: handleBeforeInput,
        }}
      />
    );
  }

  return (
    <TextField
      {...rest}
      label={label}
      value={draft}
      onFocus={(e) => {
        focusedRef.current = true;
        setDraft(formatNroCheque(value));
        onFocus?.(e);
      }}
      onChange={(e) => {
        const next = limitarNroCheque(e.target.value);
        setDraft(next);
        onChange(next);
      }}
      onBlur={(e) => {
        focusedRef.current = false;
        const padded = formatNroCheque(draft);
        setDraft(padded);
        onChange(padded);
        onBlur?.(e);
      }}
      inputProps={{
        inputMode: 'numeric',
        autoComplete: 'off',
        maxLength: NRO_CHEQUE_LEN,
        ...inputProps,
      }}
    />
  );
};

export default NroChequeTextField;
